/**
 * Following the person in a reframed export (a 9:16 Short of a 16:9 recording).
 *
 * Faces are found in the browser, with MediaPipe's face detector (loaded from a CDN
 * the first time it's used), on frames sampled through the stretches the edit keeps.
 * The video never leaves the browser for this. The faces become a few crop keyframes
 * (`reframe.track`): the person the crop is on stays chosen while they're on screen,
 * small movements are ignored, and the path is thinned to the points where it turns.
 */
import type { EditList, FocusKey } from '../editing/editList';
import { EDIT_LIMITS, playedSegments } from '../editing/editList';

export interface FaceBox {
  /** The face's centre and width, as shares (0–100) of the frame. */
  xPct: number;
  yPct: number;
  widthPct: number;
  score: number;
}

export interface FaceSample {
  /** Source time (the set's timeline for multicam). */
  atMs: number;
  faces: FaceBox[];
}

/** The frame to look at for a source time: which file, and where in it. Null where there's no picture. */
export type FrameAt = (atMs: number) => { url: string; fileMs: number } | null;

interface Detection {
  boundingBox?: { originX: number; originY: number; width: number; height: number };
  categories?: Array<{ score: number }>;
}

export interface FaceDetector {
  detect(image: HTMLVideoElement | HTMLCanvasElement): { detections: Detection[] };
  close?(): void;
}

const VISION_VERSION = '0.10.14';
const VISION_URL = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${VISION_VERSION}/vision_bundle.mjs`;
const WASM_URL = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${VISION_VERSION}/wasm`;
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite';

let detectorPromise: Promise<FaceDetector> | null = null;

/** MediaPipe's face detector, loaded once (GPU where the browser has it, else CPU). */
export function loadFaceDetector(): Promise<FaceDetector> {
  if (!detectorPromise) {
    detectorPromise = (async () => {
      const vision: any = await import(/* webpackIgnore: true */ VISION_URL);
      const fileset = await vision.FilesetResolver.forVisionTasks(WASM_URL);
      const create = (delegate: 'GPU' | 'CPU'): Promise<FaceDetector> => vision.FaceDetector.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate },
        runningMode: 'IMAGE',
        minDetectionConfidence: 0.5,
      });
      try {
        return await create('GPU');
      } catch {
        return create('CPU');
      }
    })();
    detectorPromise.catch(() => { detectorPromise = null; });
  }
  return detectorPromise;
}

/** At most this many frames are looked at (the step grows for long edits). */
export const MAX_FACE_SAMPLES = 900;

/**
 * The source times to look at: through every kept stretch, every `stepMs` (500 ms for
 * short edits, longer for long ones), plus either side of each camera cut so the crop
 * jumps with the cut instead of panning across it.
 */
export function faceSampleTimes(list: EditList, stepMs = 500): number[] {
  const segments = playedSegments(list);
  const keptMs = segments.reduce((total, segment) => total + (segment.outMs - segment.inMs), 0);
  const step = Math.max(stepMs, Math.ceil(keptMs / MAX_FACE_SAMPLES / 100) * 100);
  const times: number[] = [];
  segments.forEach((segment) => {
    for (let at = segment.inMs; at < segment.outMs; at += step) times.push(Math.round(at));
    times.push(Math.max(segment.inMs, segment.outMs - 40));
  });
  (list.multicamCut || []).forEach((cut) => {
    if (segments.some((segment) => cut.atMs > segment.inMs && cut.atMs < segment.outMs)) times.push(Math.max(0, cut.atMs - 40), cut.atMs + 40);
  });
  return Array.from(new Set(times)).sort((a, b) => a - b);
}

const waitFor = (el: HTMLVideoElement, event: string, timeoutMs: number): Promise<void> => new Promise((resolve, reject) => {
  const done = () => { clearTimeout(timer); el.removeEventListener(event, done); el.removeEventListener('error', failed); resolve(); };
  const failed = () => { clearTimeout(timer); el.removeEventListener(event, done); el.removeEventListener('error', failed); reject(new Error('The video couldn’t be read.')); };
  const timer = setTimeout(() => { el.removeEventListener(event, done); el.removeEventListener('error', failed); reject(new Error('The video took too long to load.')); }, timeoutMs);
  el.addEventListener(event, done);
  el.addEventListener('error', failed);
});

export class FaceSearchCancelled extends Error {
  constructor() {
    super('Cancelled');
    this.name = 'FaceSearchCancelled';
  }
}

/** Look for faces at each time. Throws `FaceSearchCancelled` when the signal aborts. */
export async function findFaces(options: {
  times: number[];
  frameAt: FrameAt;
  detector: FaceDetector;
  onProgress?: (done: number, total: number) => void;
  signal?: AbortSignal;
  createVideo?: () => HTMLVideoElement;
}): Promise<FaceSample[]> {
  const { times, frameAt, detector, onProgress, signal } = options;
  const createVideo = options.createVideo || (() => document.createElement('video'));
  const videos = new Map<string, HTMLVideoElement>();
  const videoFor = async (url: string): Promise<HTMLVideoElement> => {
    const known = videos.get(url);
    if (known) return known;
    const el = createVideo();
    el.crossOrigin = 'anonymous';
    el.muted = true;
    el.playsInline = true;
    el.preload = 'auto';
    const loaded = waitFor(el, 'loadeddata', 30000);
    el.src = url;
    el.load();
    await loaded;
    videos.set(url, el);
    return el;
  };
  const samples: FaceSample[] = [];
  try {
    for (let i = 0; i < times.length; i += 1) {
      if (signal?.aborted) throw new FaceSearchCancelled();
      const atMs = times[i];
      const frame = frameAt(atMs);
      if (!frame) {
        samples.push({ atMs, faces: [] });
      } else {
        const el = await videoFor(frame.url);
        const target = frame.fileMs / 1000;
        if (Math.abs(el.currentTime - target) > 0.001) {
          const seeked = waitFor(el, 'seeked', 10000);
          el.currentTime = target;
          await seeked;
        }
        const width = el.videoWidth || 1;
        const height = el.videoHeight || 1;
        const found = detector.detect(el).detections || [];
        samples.push({
          atMs,
          faces: found.filter((d) => d.boundingBox).map((d) => {
            const box = d.boundingBox as NonNullable<Detection['boundingBox']>;
            return {
              xPct: ((box.originX + box.width / 2) / width) * 100,
              yPct: ((box.originY + box.height / 2) / height) * 100,
              widthPct: (box.width / width) * 100,
              score: d.categories?.[0]?.score ?? 1,
            };
          }),
        });
      }
      onProgress?.(i + 1, times.length);
    }
  } finally {
    videos.forEach((el) => { el.removeAttribute('src'); try { el.load(); } catch { /* jsdom */ } });
  }
  return samples;
}

/** A face that has moved this far (share of the frame) is someone else, or the shot changed. */
const SAME_PERSON_PCT = 12;
/** Movements smaller than this are ignored, so the crop doesn't wobble. */
const STILL_PCT = 3;
/** Where the thinned path may differ from the person's, at most. */
const PATH_TOLERANCE_PCT = 1.5;

const clampPct = (value: number): number => Math.round(Math.min(Math.max(value, 0), 100) * 10) / 10;

/** Ramer–Douglas–Peucker on (time, x, y): keep the points where the path turns. */
function thin(points: FocusKey[], tolerance: number): FocusKey[] {
  if (points.length <= 2) return points;
  const keep = new Array(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  const stack: Array<[number, number]> = [[0, points.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop() as [number, number];
    let worst = -1;
    let worstAt = -1;
    for (let i = a + 1; i < b; i += 1) {
      const share = (points[i].atMs - points[a].atMs) / Math.max(1, points[b].atMs - points[a].atMs);
      const x = points[a].focusXPct + (points[b].focusXPct - points[a].focusXPct) * share;
      const y = points[a].focusYPct + (points[b].focusYPct - points[a].focusYPct) * share;
      const off = Math.max(Math.abs(points[i].focusXPct - x), Math.abs(points[i].focusYPct - y));
      if (off > worst) { worst = off; worstAt = i; }
    }
    if (worst > tolerance && worstAt > 0) {
      keep[worstAt] = true;
      stack.push([a, worstAt], [worstAt, b]);
    }
  }
  return points.filter((_, i) => keep[i]);
}

/**
 * Crop keyframes from the faces found. The crop stays on the person it's on while
 * they're in view (unless someone much larger, nearer the camera, appears); it holds
 * where the person was through frames with no face. Empty when no face was found.
 */
export function trackFromFaces(samples: FaceSample[]): FocusKey[] {
  const chosen: FocusKey[] = [];
  let current: FaceBox | null = null;
  samples.forEach((sample) => {
    const faces = sample.faces.filter((face) => face.score >= 0.5);
    if (faces.length === 0) return;
    const largest = faces.reduce((best, face) => (face.widthPct > best.widthPct ? face : best), faces[0]);
    let pick = largest;
    if (current) {
      const from = current;
      const near = faces
        .filter((face) => Math.hypot(face.xPct - from.xPct, face.yPct - from.yPct) <= SAME_PERSON_PCT)
        .sort((p, q) => Math.hypot(p.xPct - from.xPct, p.yPct - from.yPct) - Math.hypot(q.xPct - from.xPct, q.yPct - from.yPct))[0];
      if (near && largest.widthPct < near.widthPct * 1.5) pick = near;
    }
    current = pick;
    chosen.push({ atMs: sample.atMs, focusXPct: pick.xPct, focusYPct: pick.yPct });
  });
  if (chosen.length === 0) return [];
  // A median of three steadies one-frame misses; then small moves are held still.
  const steady = chosen.map((point, i) => {
    if (i === 0 || i === chosen.length - 1) return point;
    const median = (a: number, b: number, c: number) => a + b + c - Math.min(a, b, c) - Math.max(a, b, c);
    return { ...point, focusXPct: median(chosen[i - 1].focusXPct, point.focusXPct, chosen[i + 1].focusXPct), focusYPct: median(chosen[i - 1].focusYPct, point.focusYPct, chosen[i + 1].focusYPct) };
  });
  const held: FocusKey[] = [];
  let at = steady[0];
  steady.forEach((point) => {
    if (Math.abs(point.focusXPct - at.focusXPct) > STILL_PCT || Math.abs(point.focusYPct - at.focusYPct) > STILL_PCT) at = point;
    held.push({ atMs: point.atMs, focusXPct: at.focusXPct, focusYPct: at.focusYPct });
  });
  // A jump (a cut, or someone else) happens at once rather than as a pan between samples.
  const withJumps: FocusKey[] = [];
  held.forEach((point, i) => {
    const before = held[i - 1];
    if (before && Math.abs(point.focusXPct - before.focusXPct) > SAME_PERSON_PCT && point.atMs - before.atMs > 80) {
      withJumps.push({ atMs: point.atMs - 40, focusXPct: before.focusXPct, focusYPct: before.focusYPct });
    }
    withJumps.push(point);
  });
  const thinned = thin(withJumps, PATH_TOLERANCE_PCT).map((point) => ({ atMs: Math.round(point.atMs), focusXPct: clampPct(point.focusXPct), focusYPct: clampPct(point.focusYPct) }));
  const ordered = thinned.filter((point, i) => i === 0 || point.atMs > thinned[i - 1].atMs);
  if (ordered.length <= EDIT_LIMITS.maxFocusKeys) return ordered;
  // Over the limit (very long edits): keep evenly spaced keyframes.
  const every = ordered.length / EDIT_LIMITS.maxFocusKeys;
  return Array.from({ length: EDIT_LIMITS.maxFocusKeys }, (_, i) => ordered[Math.floor(i * every)]);
}

/** Put the crop at a point at one moment of a moving crop: replace a keyframe within 250 ms, else add one. */
export function setFocusKey(track: FocusKey[], atMs: number, focusXPct: number, focusYPct: number): FocusKey[] {
  const key = { atMs: Math.max(0, Math.round(atMs)), focusXPct: clampPct(focusXPct), focusYPct: clampPct(focusYPct) };
  const near = track.findIndex((existing) => Math.abs(existing.atMs - key.atMs) <= 250);
  if (near >= 0) return track.map((existing, i) => (i === near ? { ...key, atMs: existing.atMs } : existing));
  return [...track, key].sort((a, b) => a.atMs - b.atMs);
}
